import { useState, useEffect, useRef, useCallback } from "react";

const COLORS = {
  bg: "#0a0e1a",
  bgCard: "#111827",
  bgCardAlt: "#1a2236",
  border: "#1e293b",
  text: "#e2e8f0",
  textMuted: "#94a3b8",
  textDim: "#64748b",
  accent: "#3b82f6",
  accentGlow: "rgba(59,130,246,0.15)",
  success: "#22c55e",
  successGlow: "rgba(34,197,94,0.15)",
  warning: "#f59e0b",
  warningGlow: "rgba(245,158,11,0.15)",
  danger: "#ef4444",
  dangerGlow: "rgba(239,68,68,0.15)",
  purple: "#a78bfa",
  purpleGlow: "rgba(167,139,250,0.15)",
  cyan: "#22d3ee",
  teal: "#14b8a6",
  tealGlow: "rgba(20,184,166,0.15)",
};

const FONT = `'JetBrains Mono','Fira Code','SF Mono',monospace`;
const FONT_BODY = `'DM Sans','Segoe UI',system-ui,sans-serif`;

const layout = {
  app: { background: COLORS.bg, minHeight: "100vh", color: COLORS.text, fontFamily: FONT_BODY, fontSize: 14 },
  sidebar: { width: 240, background: COLORS.bgCard, borderRight: `1px solid ${COLORS.border}`, height: "calc(100vh - 70px)", position: "fixed", top: 70, left: 0, overflowY: "auto", zIndex: 100 },
  main: { marginLeft: 240, minHeight: "100vh", padding: "32px 40px", maxWidth: 1000 },
  navItem: (active) => ({ display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", cursor: "pointer", borderRadius: 8, margin: "2px 8px", background: active ? COLORS.accentGlow : "transparent", color: active ? COLORS.accent : COLORS.textMuted, fontWeight: active ? 600 : 400, fontSize: 13, transition: "all .18s", borderLeft: active ? `3px solid ${COLORS.accent}` : "3px solid transparent" }),
  badge: (col) => ({ display: "inline-block", padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, background: col + "22", color: col, border: `1px solid ${col}44`, letterSpacing: 0.3 }),
};

// ── SECTION 1: CONCEPT CARDS ─────────────────────────────────────────────────
function ConceptSection() {
  const [activeCard, setActiveCard] = useState(null);
  const concepts = [
    {
      icon: "🪪",
      title: "What IS a TLS Certificate?",
      color: COLORS.accent,
      glow: COLORS.accentGlow,
      content: `A TLS certificate is a digital identity card. It proves "I am who I say I am" AND contains a public key others can use to encrypt data only you can read. In Kubernetes every component — API Server, etcd, kubelet, scheduler — carries one.`,
    },
    {
      icon: "🏛️",
      title: "Certificate Authority (CA)",
      color: COLORS.purple,
      glow: COLORS.purpleGlow,
      content: `The CA is the trusted stamp-of-approval office. It signs every certificate in the cluster. When kubelet shows its cert to the API Server, the API Server trusts it because the CA signed it. kubeadm auto-creates your cluster CA under /etc/kubernetes/pki/ca.crt.`,
    },
    {
      icon: "🔐",
      title: "mTLS — Both Sides Prove Identity",
      color: COLORS.teal,
      glow: COLORS.tealGlow,
      content: `Normal TLS: only the server proves identity (like HTTPS). Mutual TLS (mTLS): BOTH the client AND server show certificates. Every Kubernetes component connection is mTLS — kubectl proves it's you, and the API Server proves it's the real API Server.`,
    },
    {
      icon: "📁",
      title: "The PKI Directory",
      color: COLORS.warning,
      glow: COLORS.warningGlow,
      content: `kubeadm stores all cluster certificates under /etc/kubernetes/pki/. You'll find ca.crt, ca.key, apiserver.crt, apiserver.key, etcd/ca.crt and more. This is your cluster's entire trust chain in one directory.`,
    },
  ];

  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: FONT, fontSize: 18, color: COLORS.cyan, marginBottom: 6, letterSpacing: 2 }}>
        01 — THE BIG PICTURE
      </h2>
      <p style={{ fontFamily: FONT_BODY, color: COLORS.textMuted, fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
        Click each card to understand what TLS certificates actually are before diving into commands.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        {concepts.map((c, i) => (
          <div
            key={i}
            onClick={() => setActiveCard(activeCard === i ? null : i)}
            style={{
              background: activeCard === i ? c.glow : COLORS.bgCard,
              border: `1px solid ${activeCard === i ? c.color : COLORS.border}`,
              borderRadius: 10, padding: 16, cursor: "pointer",
              transition: "all 0.3s",
              transform: activeCard === i ? "scale(1.02)" : "scale(1)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 22 }}>{c.icon}</span>
              <span style={{ fontFamily: FONT, fontSize: 13, color: c.color, fontWeight: 700 }}>{c.title}</span>
            </div>
            <p style={{
              fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.65,
              maxHeight: activeCard === i ? 200 : 0, overflow: "hidden",
              transition: "max-height 0.4s ease", margin: 0,
            }}>
              {c.content}
            </p>
            {activeCard !== i && (
              <span style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim }}>tap to expand ▾</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SECTION 2: PASSPORT ANALOGY ──────────────────────────────────────────────
function AnalogySection() {
  const [step, setStep] = useState(0);
  const steps = [
    {
      emoji: "🛂",
      title: "The Passport Analogy",
      text: "Think of TLS certificates as passports. The Certificate Authority (CA) is like a government — the only entity everyone agrees to trust. Every Kubernetes component gets its own 'passport' signed by the CA.",
      highlight: COLORS.accent,
    },
    {
      emoji: "🪪",
      title: "Step 1: Issuing Passports",
      text: "kubeadm init creates your cluster CA (the government). Then it generates certificates (passports) for the API Server, etcd, scheduler, and controller-manager — all signed by that CA.",
      highlight: COLORS.purple,
    },
    {
      emoji: "✈️",
      title: "Step 2: Crossing the Border",
      text: "When kubectl talks to the API Server, both sides show passports. The API Server checks: 'Is this kubectl cert signed by my trusted CA?' kubectl checks: 'Is this API Server cert signed by my trusted CA?' Both must pass.",
      highlight: COLORS.teal,
    },
    {
      emoji: "🔄",
      title: "Step 3: Passport Expiry",
      text: "K8s certificates expire after 1 year by default. Expired = connection refused. kubeadm certs renew refreshes them. Production clusters use cert-manager for automatic renewal — like auto-renewing your passport before it expires.",
      highlight: COLORS.warning,
    },
  ];
  const s = steps[step];

  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: FONT, fontSize: 18, color: COLORS.cyan, marginBottom: 6, letterSpacing: 2 }}>
        02 — THE PASSPORT ANALOGY
      </h2>
      <p style={{ fontFamily: FONT_BODY, color: COLORS.textMuted, fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
        Every TLS cert in K8s works exactly like a government-issued passport — identity plus a trust chain.
      </p>
      <div style={{
        background: COLORS.bgCard, border: `1px solid ${s.highlight}`, borderRadius: 12,
        padding: 24, position: "relative", overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", top: 0, left: 0,
          width: `${((step + 1) / steps.length) * 100}%`,
          height: 3, background: s.highlight, transition: "width 0.5s ease", borderRadius: 2,
        }} />
        <div style={{ fontSize: 48, marginBottom: 12, textAlign: "center" }}>{s.emoji}</div>
        <h3 style={{ fontFamily: FONT, fontSize: 15, color: s.highlight, textAlign: "center", marginBottom: 10 }}>{s.title}</h3>
        <p style={{ fontFamily: FONT_BODY, fontSize: 13.5, color: COLORS.text, textAlign: "center", lineHeight: 1.7, maxWidth: 500, margin: "0 auto 16px" }}>
          {s.text}
        </p>
        <div style={{ display: "flex", justifyContent: "center", gap: 8 }}>
          {steps.map((_, i) => (
            <button key={i} onClick={() => setStep(i)} style={{
              width: i === step ? 28 : 10, height: 10, borderRadius: 5, border: "none", cursor: "pointer",
              background: i === step ? s.highlight : COLORS.border, transition: "all 0.3s",
            }} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ── SECTION 3: PKI TRUST CHAIN EXPLORER ──────────────────────────────────────
function PKIExplorer() {
  const [selected, setSelected] = useState(null);

  const certs = [
    {
      id: "cluster-ca",
      file: "/etc/kubernetes/pki/ca.crt\n/etc/kubernetes/pki/ca.key",
      label: "Cluster CA",
      icon: "🏛️",
      color: COLORS.accent,
      glow: COLORS.accentGlow,
      level: 0,
      cn: "kubernetes",
      signsCount: 4,
      desc: "The root of all trust. Signs every other certificate in the control plane. The .key must NEVER leave the master node. Compromise = full cluster re-bootstrap.",
    },
    {
      id: "etcd-ca",
      file: "/etc/kubernetes/pki/etcd/ca.crt\n/etc/kubernetes/pki/etcd/ca.key",
      label: "etcd CA",
      icon: "🗄️",
      color: COLORS.purple,
      glow: COLORS.purpleGlow,
      level: 0,
      cn: "etcd-ca",
      signsCount: 3,
      desc: "A SEPARATE CA just for etcd. etcd is so critical it gets its own isolated CA. Compromise of the cluster CA does NOT automatically compromise etcd.",
    },
    {
      id: "front-proxy-ca",
      file: "/etc/kubernetes/pki/front-proxy-ca.crt\n/etc/kubernetes/pki/front-proxy-ca.key",
      label: "Front Proxy CA",
      icon: "🔀",
      color: COLORS.warning,
      glow: COLORS.warningGlow,
      level: 0,
      cn: "front-proxy-ca",
      signsCount: 1,
      desc: "Third CA for API aggregation. Used by the API Server to authenticate API extension servers like metrics-server. A completely separate trust domain.",
    },
    {
      id: "apiserver",
      file: "/etc/kubernetes/pki/apiserver.crt\n/etc/kubernetes/pki/apiserver.key",
      label: "API Server",
      icon: "🌐",
      color: COLORS.cyan,
      glow: "rgba(34,211,238,0.15)",
      level: 1,
      cn: "kube-apiserver",
      signsCount: 0,
      desc: "The API Server's TLS cert. Used in every HTTPS connection — kubectl, kubelet, webhooks. SANs must include the service IP (10.96.0.1) and all master hostnames/IPs.",
    },
    {
      id: "apiserver-kubelet",
      file: "/etc/kubernetes/pki/apiserver-kubelet-client.crt\n/etc/kubernetes/pki/apiserver-kubelet-client.key",
      label: "API→Kubelet",
      icon: "🔗",
      color: COLORS.teal,
      glow: COLORS.tealGlow,
      level: 1,
      cn: "kube-apiserver-kubelet-client",
      signsCount: 0,
      desc: "When the API Server calls kubelet (e.g. kubectl exec, kubectl logs), it presents THIS cert to prove its identity to the kubelet.",
    },
    {
      id: "etcd-server",
      file: "/etc/kubernetes/pki/etcd/server.crt\n/etc/kubernetes/pki/etcd/server.key",
      label: "etcd Server",
      icon: "🗃️",
      color: COLORS.success,
      glow: COLORS.successGlow,
      level: 1,
      cn: "master-node-hostname",
      signsCount: 0,
      desc: "etcd's own TLS cert for accepting connections. The API Server presents apiserver-etcd-client.crt when connecting here — etcd validates it against the etcd CA.",
    },
    {
      id: "sa",
      file: "/etc/kubernetes/pki/sa.pub\n/etc/kubernetes/pki/sa.key",
      label: "Service Acct",
      icon: "🤖",
      color: COLORS.danger,
      glow: COLORS.dangerGlow,
      level: 1,
      cn: "N/A (key pair only)",
      signsCount: 0,
      desc: "Not a certificate — a key pair for ServiceAccount JWT tokens. sa.key signs new tokens; sa.pub verifies them. Rotating without a transition period breaks every running pod.",
    },
    {
      id: "scheduler",
      file: "/etc/kubernetes/scheduler.conf\n(cert embedded in kubeconfig)",
      label: "Scheduler",
      icon: "📅",
      color: COLORS.purple,
      glow: COLORS.purpleGlow,
      level: 1,
      cn: "system:kube-scheduler",
      signsCount: 0,
      desc: "Embedded in scheduler.conf kubeconfig. The CN 'system:kube-scheduler' maps directly to an RBAC ClusterRole. CN always defines the Kubernetes identity.",
    },
  ];

  const sel = certs.find(c => c.id === selected);

  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: FONT, fontSize: 18, color: COLORS.cyan, marginBottom: 6, letterSpacing: 2 }}>
        03 — PKI TRUST CHAIN EXPLORER
      </h2>
      <p style={{ fontFamily: FONT_BODY, color: COLORS.textMuted, fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
        Click any certificate to inspect its file path, Common Name, and purpose in the cluster.
      </p>

      {/* CA row */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, marginBottom: 6, letterSpacing: 1 }}>
          CERTIFICATE AUTHORITIES — ROOT TRUST
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
          {certs.filter(c => c.level === 0).map(c => (
            <div key={c.id} onClick={() => setSelected(selected === c.id ? null : c.id)} style={{
              background: selected === c.id ? c.glow : COLORS.bgCard,
              border: `2px solid ${selected === c.id ? c.color : COLORS.border}`,
              borderRadius: 10, padding: "14px", cursor: "pointer", transition: "all 0.25s",
            }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{c.icon}</div>
              <div style={{ fontFamily: FONT, fontSize: 12, color: c.color, fontWeight: 700, marginBottom: 2 }}>{c.label}</div>
              <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim }}>
                signs {c.signsCount} cert(s)
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Connector line */}
      <div style={{ display: "flex", justifyContent: "center", gap: 4, marginBottom: 8, paddingTop: 4 }}>
        {[...Array(11)].map((_, i) => (
          <div key={i} style={{ width: 4, height: 4, borderRadius: 2, background: COLORS.border }} />
        ))}
      </div>

      {/* Leaf certs — 4-column to avoid overflow at 720px */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, marginBottom: 6, letterSpacing: 1 }}>
          LEAF CERTIFICATES — COMPONENT IDENTITIES
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
          {certs.filter(c => c.level === 1).map(c => (
            <div key={c.id} onClick={() => setSelected(selected === c.id ? null : c.id)} style={{
              background: selected === c.id ? c.glow : COLORS.bgCard,
              border: `1px solid ${selected === c.id ? c.color : COLORS.border}`,
              borderRadius: 8, padding: "10px 12px", cursor: "pointer", transition: "all 0.25s",
            }}>
              <div style={{ fontSize: 18, marginBottom: 4 }}>{c.icon}</div>
              <div style={{ fontFamily: FONT, fontSize: 11, color: c.color, fontWeight: 700, lineHeight: 1.3 }}>{c.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Detail panel */}
      {sel && (
        <div style={{
          background: sel.glow, border: `1px solid ${sel.color}`,
          borderRadius: 10, padding: 18, transition: "all 0.3s",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 28 }}>{sel.icon}</span>
            <div>
              <div style={{ fontFamily: FONT, fontSize: 15, color: sel.color, fontWeight: 700 }}>{sel.label}</div>
              <div style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textMuted }}>CN = {sel.cn}</div>
            </div>
          </div>
          <div style={{
            background: "#000", borderRadius: 6, padding: "10px 14px", marginBottom: 12,
            fontFamily: FONT, fontSize: 11.5, color: COLORS.success, whiteSpace: "pre-wrap",
            lineHeight: 1.7, border: `1px solid ${COLORS.border}`,
          }}>{sel.file}</div>
          <p style={{ fontFamily: FONT_BODY, fontSize: 13, color: COLORS.text, lineHeight: 1.65, margin: 0 }}>
            💡 {sel.desc}
          </p>
        </div>
      )}
    </div>
  );
}

// ── SECTION 4: mTLS HANDSHAKE SIMULATOR ──────────────────────────────────────
const HANDSHAKE_PHASES = [
  { id: "idle",           label: "kubectl → API Server (idle)",               duration: 1800 },
  { id: "client-hello",   label: "① Client Hello — supported cipher suites",  duration: 2200 },
  { id: "server-cert",    label: "② Server sends apiserver.crt",              duration: 2500 },
  { id: "client-verify",  label: "③ Client verifies: CA signed this cert?",   duration: 2000 },
  { id: "client-cert",    label: "④ Server requests client cert (mTLS)",      duration: 2200 },
  { id: "server-verify",  label: "⑤ Server verifies client cert against CA",  duration: 2000 },
  { id: "session-key",    label: "⑥ Both derive symmetric session key",       duration: 2200 },
  { id: "encrypted",      label: "⑦ Encrypted traffic flows ✓",              duration: 2800 },
];

const PHASE_DETAILS = {
  "idle":          { arrow: null,  color: COLORS.textDim,  note: "Connection not yet established — no certs shown yet" },
  "client-hello":  { arrow: "→",   color: COLORS.accent,   note: "Hi! I support TLS 1.3. Here are my supported cipher suites." },
  "server-cert":   { arrow: "←",   color: COLORS.purple,   note: "Here is my certificate (apiserver.crt) — check the CA signature." },
  "client-verify": { arrow: "⬌",   color: COLORS.warning,  note: "Is this cert signed by my trusted ca.crt? ✓ YES — API Server is genuine." },
  "client-cert":   { arrow: "→",   color: COLORS.teal,     note: "Server: send me YOUR cert too (mTLS). kubectl sends client.crt." },
  "server-verify": { arrow: "⬌",   color: COLORS.warning,  note: "Is kubectl cert signed by my CA? ✓ YES — CN tells me the username for RBAC." },
  "session-key":   { arrow: "⬌",   color: COLORS.success,  note: "Key exchange complete — both sides now share an AES symmetric session key." },
  "encrypted":     { arrow: "⟺",   color: COLORS.success,  note: "All traffic is AES-256 encrypted. Both identities verified. ✓" },
};

function HandshakeSimulator() {
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const progressRef = useRef(null);

  const stopSim = useCallback(() => {
    setRunning(false);
    clearTimeout(timerRef.current);
    clearInterval(progressRef.current);
  }, []);

  const advancePhase = useCallback((idx) => {
    if (idx >= HANDSHAKE_PHASES.length) { stopSim(); return; }
    setPhaseIdx(idx);
    setProgress(0);
    const dur = HANDSHAKE_PHASES[idx].duration;
    let elapsed = 0;
    progressRef.current = setInterval(() => {
      elapsed += 50;
      setProgress(Math.min((elapsed / dur) * 100, 100));
    }, 50);
    timerRef.current = setTimeout(() => {
      clearInterval(progressRef.current);
      advancePhase(idx + 1);
    }, dur);
  }, [stopSim]);

  const startSim = () => { setPhaseIdx(0); setRunning(true); advancePhase(0); };
  const resetSim = () => { stopSim(); setPhaseIdx(0); setProgress(0); };
  useEffect(() => () => { clearTimeout(timerRef.current); clearInterval(progressRef.current); }, []);

  const phase = HANDSHAKE_PHASES[phaseIdx];
  const pd = PHASE_DETAILS[phase.id] || PHASE_DETAILS["idle"];

  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: FONT, fontSize: 18, color: COLORS.cyan, marginBottom: 6, letterSpacing: 2 }}>
        04 — mTLS HANDSHAKE SIMULATOR
      </h2>
      <p style={{ fontFamily: FONT_BODY, color: COLORS.textMuted, fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
        Watch exactly what happens when kubectl connects to the API Server — step by step.
      </p>

      <div style={{ background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 20 }}>
        <div style={{ fontFamily: FONT, fontSize: 13, color: COLORS.text, marginBottom: 6, minHeight: 22 }}>
          {phase.label}
        </div>
        <div style={{ height: 4, background: COLORS.border, borderRadius: 2, overflow: "hidden", marginBottom: 20 }}>
          <div style={{ width: `${progress}%`, height: "100%", background: pd.color, borderRadius: 2, transition: "width 0.05s linear" }} />
        </div>

        {/* Actors row */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20, gap: 8 }}>
          <div style={{
            background: COLORS.bgCardAlt, border: `2px solid ${COLORS.accent}`,
            borderRadius: 10, padding: "14px 18px", textAlign: "center", minWidth: 110,
          }}>
            <div style={{ fontSize: 28, marginBottom: 4 }}>💻</div>
            <div style={{ fontFamily: FONT, fontSize: 12, color: COLORS.accent, fontWeight: 700 }}>kubectl</div>
            <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>
              {phaseIdx >= 4 ? "client.crt ✓" : phaseIdx >= 2 ? "checking…" : "no cert shown"}
            </div>
          </div>

          <div style={{ flex: 1, textAlign: "center" }}>
            {pd.arrow && (
              <div style={{ fontFamily: FONT, fontSize: 24, color: pd.color, marginBottom: 6, transition: "color 0.4s" }}>
                {pd.arrow}
              </div>
            )}
            <div style={{
              background: COLORS.bgCardAlt, border: `1px solid ${pd.color}44`,
              borderRadius: 8, padding: "8px 10px",
              fontFamily: FONT_BODY, fontSize: 11.5, color: COLORS.textMuted, lineHeight: 1.5,
            }}>
              {pd.note}
            </div>
          </div>

          <div style={{
            background: COLORS.bgCardAlt, border: `2px solid ${COLORS.purple}`,
            borderRadius: 10, padding: "14px 18px", textAlign: "center", minWidth: 110,
          }}>
            <div style={{ fontSize: 28, marginBottom: 4 }}>🌐</div>
            <div style={{ fontFamily: FONT, fontSize: 12, color: COLORS.purple, fontWeight: 700 }}>API Server</div>
            <div style={{ fontFamily: FONT, fontSize: 10, color: COLORS.textDim, marginTop: 2 }}>
              {phaseIdx >= 1 ? "apiserver.crt ✓" : "waiting…"}
            </div>
          </div>
        </div>

        {/* Phase dots */}
        <div style={{ display: "flex", justifyContent: "center", gap: 5, marginBottom: 16 }}>
          {HANDSHAKE_PHASES.map((_, i) => (
            <div key={i} style={{
              width: 8, height: 8, borderRadius: 4,
              background: i < phaseIdx ? COLORS.success : i === phaseIdx ? pd.color : COLORS.border,
              transition: "background 0.3s",
            }} />
          ))}
        </div>

        <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
          <button onClick={running ? stopSim : startSim} style={{
            fontFamily: FONT, fontSize: 13, padding: "8px 22px", borderRadius: 6,
            border: "none", cursor: "pointer", fontWeight: 700,
            background: running ? COLORS.danger : COLORS.accent, color: "#fff",
          }}>
            {running ? "⏸ Pause" : phaseIdx > 0 && !running ? "▶ Resume" : "▶ Start Handshake"}
          </button>
          <button onClick={resetSim} style={{
            fontFamily: FONT, fontSize: 13, padding: "8px 16px", borderRadius: 6,
            border: `1px solid ${COLORS.border}`, cursor: "pointer",
            background: "transparent", color: COLORS.textMuted,
          }}>
            ↺ Reset
          </button>
        </div>
      </div>
    </div>
  );
}

// ── SECTION 5: COMMANDS ───────────────────────────────────────────────────────
function CommandsSection() {
  const [openStep, setOpenStep] = useState(null);

  const steps = [
    {
      num: "01", title: "Inspect All Cluster Certs", icon: "🔍", color: COLORS.accent,
      cmd: "kubeadm certs check-expiration",
      flags: [{ f: "certs check-expiration", d: "Lists every cert, its expiry date, and which CA signed it. Run this FIRST before any cert work — it shows everything at a glance." }],
      explain: "Like checking all your passports before a world tour. Shows you which ones expire soon so you're never caught off guard.",
    },
    {
      num: "02", title: "Decode a Certificate", icon: "🔬", color: COLORS.purple,
      cmd: "openssl x509 -in /etc/kubernetes/pki/apiserver.crt \\\n  -text -noout",
      flags: [
        { f: "-in <file>", d: "Path to the certificate file (.crt or .pem)" },
        { f: "-text", d: "Print full human-readable details: Subject, Issuer, SANs, validity dates, public key algorithm" },
        { f: "-noout", d: "Suppress the raw base64 output — show only the human-readable text" },
      ],
      explain: "Opens the certificate and reads every field — who signed it, what names it covers (SANs), when it expires. Essential for debugging TLS errors.",
    },
    {
      num: "03", title: "Check Certificate Expiry Only", icon: "📅", color: COLORS.warning,
      cmd: "openssl x509 -in /etc/kubernetes/pki/apiserver.crt \\\n  -noout -enddate",
      flags: [{ f: "-enddate", d: "Prints only the 'Not After' field — the expiry date. Much faster than -text when you only need the date." }],
      explain: "Quick expiry check for any cert. Ideal in a monitoring script: alert when within 30 days of expiry.",
    },
    {
      num: "04", title: "Renew ALL Certificates", icon: "🔄", color: COLORS.success,
      cmd: "# Run on master node as root\nkubeadm certs renew all",
      flags: [{ f: "certs renew all", d: "Renews every kubeadm-managed certificate at once — adds 1 year from today. You MUST restart control plane components after." }],
      explain: "The go-to annual cert renewal command. After running, restart kube-apiserver, scheduler, controller-manager, and etcd. They cache certs in memory.",
    },
    {
      num: "05", title: "Renew a Single Cert", icon: "🎯", color: COLORS.teal,
      cmd: "kubeadm certs renew apiserver\nkubeadm certs renew apiserver-kubelet-client\nkubeadm certs renew front-proxy-client",
      flags: [
        { f: "apiserver", d: "Renews only /etc/kubernetes/pki/apiserver.crt + apiserver.key" },
        { f: "apiserver-kubelet-client", d: "Renews the cert the API Server uses to call kubelet" },
        { f: "front-proxy-client", d: "Renews the API aggregation proxy client cert" },
      ],
      explain: "Targeted renewal when one specific cert is expiring. Run 'kubeadm certs renew --help' to list all valid cert names.",
    },
    {
      num: "06", title: "Generate a User CSR (Manual)", icon: "📝", color: COLORS.cyan,
      cmd: "# Step 1: generate private key\nopenssl genrsa -out jane.key 2048\n\n# Step 2: create Certificate Signing Request\nopenssl req -new -key jane.key \\\n  -subj \"/CN=jane/O=dev-team\" \\\n  -out jane.csr",
      flags: [
        { f: "genrsa -out jane.key 2048", d: "Generate a 2048-bit RSA private key for user jane" },
        { f: "-subj \"/CN=jane/O=dev-team\"", d: "CN becomes the Kubernetes username; O becomes the RBAC group. Getting this wrong = RBAC never applies." },
        { f: "-out jane.csr", d: "Output the Certificate Signing Request to submit to Kubernetes" },
      ],
      explain: "How you create new user certificates manually. CN=jane is what RBAC sees as the username — map it in a RoleBinding subject.",
    },
    {
      num: "07", title: "Submit CSR to Kubernetes", icon: "📨", color: COLORS.accent,
      cmd: `cat <<EOF | kubectl apply -f -
apiVersion: certificates.k8s.io/v1
kind: CertificateSigningRequest
metadata:
  name: jane
spec:
  request: $(cat jane.csr | base64 | tr -d '\\n')
  signerName: kubernetes.io/kube-apiserver-client
  usages:
  - client auth
EOF`,
      flags: [
        { f: "signerName: kubernetes.io/kube-apiserver-client", d: "Sign this as a client auth certificate for authenticating to the API Server" },
        { f: "usages: client auth", d: "Defines what the cert can be used for. Must match what the signer allows." },
      ],
      explain: "The Kubernetes-native way to sign user certs. After submitting, an admin runs: kubectl certificate approve jane",
    },
    {
      num: "08", title: "Approve and Extract Signed Cert", icon: "✅", color: COLORS.success,
      cmd: "# Admin approves the CSR\nkubectl certificate approve jane\n\n# Extract the signed certificate\nkubectl get csr jane \\\n  -o jsonpath='{.status.certificate}' \\\n  | base64 -d > jane.crt",
      flags: [
        { f: "certificate approve", d: "The CA signs the CSR — signed cert is stored in the CSR object's .status.certificate field" },
        { f: "-o jsonpath='{.status.certificate}'", d: "Extract just the base64-encoded signed cert from the CSR status" },
        { f: "base64 -d", d: "Decode from base64 to a real PEM certificate file" },
      ],
      explain: "After approval the signed cert lives in the CSR object. Extract jane.crt + hand it to jane with jane.key. She can now kubectl with full RBAC identity.",
    },
    {
      num: "09", title: "Verify Cert Against CA", icon: "🛡️", color: COLORS.purple,
      cmd: "openssl verify \\\n  -CAfile /etc/kubernetes/pki/ca.crt \\\n  /etc/kubernetes/pki/apiserver.crt\n# Output: /etc/kubernetes/pki/apiserver.crt: OK",
      flags: [
        { f: "-CAfile", d: "The trusted CA cert to verify against. Must be the exact CA that signed the cert you're checking." },
      ],
      explain: "Cryptographically confirms a cert was signed by the expected CA. Returns OK on success. If it fails, the cert is invalid or was signed by the wrong CA.",
    },
    {
      num: "10", title: "Restart Control Plane After Renewal", icon: "🔁", color: COLORS.warning,
      cmd: "# Move static pod manifests out — kubelet stops the pods\nmv /etc/kubernetes/manifests/*.yaml /tmp/\nsleep 20\n\n# Move them back — kubelet restarts pods with new certs\nmv /tmp/*.yaml /etc/kubernetes/manifests/",
      flags: [
        { f: "/etc/kubernetes/manifests/", d: "Static Pod directory. kubelet watches it — removing a manifest kills the pod, adding it starts the pod with new on-disk certs." },
        { f: "sleep 20", d: "Give kubelet time to detect the removed files and stop the containers before restarting them." },
      ],
      explain: "Control plane components cache certs in memory. After renewal they must restart to pick up the new certs from disk — or the renewal has no effect.",
    },
  ];

  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: FONT, fontSize: 18, color: COLORS.cyan, marginBottom: 6, letterSpacing: 2 }}>
        05 — COMMANDS & FLAGS EXPLAINED
      </h2>
      <p style={{ fontFamily: FONT_BODY, color: COLORS.textMuted, fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
        Every command you'll need — click to see the exact flags and why they exist.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {steps.map((s, i) => {
          const isOpen = openStep === i;
          return (
            <div key={i} onClick={() => setOpenStep(isOpen ? null : i)} style={{
              background: isOpen ? `${s.color}11` : COLORS.bgCard,
              border: `1px solid ${isOpen ? s.color : COLORS.border}`,
              borderRadius: 8, padding: "12px 16px", cursor: "pointer", transition: "all 0.3s",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim, minWidth: 20 }}>{s.num}</span>
                <span style={{ fontSize: 18 }}>{s.icon}</span>
                <span style={{ fontFamily: FONT, fontSize: 13, color: s.color, fontWeight: 600, flex: 1 }}>{s.title}</span>
                <span style={{ fontSize: 12, color: COLORS.textDim, transform: isOpen ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.3s" }}>▾</span>
              </div>
              {isOpen && (
                <div style={{ marginTop: 12 }}>
                  <div style={{
                    background: "#000", borderRadius: 6, padding: "10px 14px", marginBottom: 10,
                    fontFamily: FONT, fontSize: 11.5, color: COLORS.success, whiteSpace: "pre-wrap",
                    lineHeight: 1.8, border: `1px solid ${COLORS.border}`,
                  }}>
                    {s.cmd}
                  </div>
                  <div style={{ marginBottom: 10 }}>
                    {s.flags.map((fl, fi) => (
                      <div key={fi} style={{ display: "flex", gap: 10, marginBottom: 6, alignItems: "flex-start" }}>
                        <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.cyan, whiteSpace: "nowrap" }}>{fl.f}</span>
                        <span style={{ fontFamily: FONT_BODY, fontSize: 12, color: COLORS.textMuted, lineHeight: 1.5 }}>{fl.d}</span>
                      </div>
                    ))}
                  </div>
                  <p style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.65, margin: 0 }}>
                    💡 {s.explain}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── SECTION 6: COMMON MISTAKES ────────────────────────────────────────────────
function MistakesSection() {
  const [open, setOpen] = useState(null);
  const mistakes = [
    {
      icon: "💀", color: COLORS.danger,
      title: "Cert expired — cluster unreachable",
      fix: "# Check expiry before disaster strikes\nkubeadm certs check-expiration\n\n# Renew everything\nkubeadm certs renew all\n\n# If API Server is already down, use local kubeconfig\nexport KUBECONFIG=/etc/kubernetes/admin.conf",
      desc: "The most common production incident. API Server returns 'x509: certificate has expired'. Schedule annual renewal or deploy cert-manager to auto-renew.",
    },
    {
      icon: "🚫", color: COLORS.warning,
      title: "Wrong CN/O in user certificate",
      fix: "# Check what CN your cert actually has\nopenssl x509 -in user.crt -noout -subject\n# subject=CN=wrong-name, O=wrong-group\n\n# Fix: regenerate CSR with correct subject\nopenssl req -new -key user.key \\\n  -subj \"/CN=correct-name/O=correct-group\" \\\n  -out user.csr",
      desc: "RBAC matches on CN (username) and O (group). If wrong, RoleBindings never apply. The cert is cryptographically valid but RBAC denies every request.",
    },
    {
      icon: "🔗", color: COLORS.purple,
      title: "Missing SAN in apiserver cert",
      fix: "# Check existing SANs\nopenssl x509 -in /etc/kubernetes/pki/apiserver.crt \\\n  -noout -ext subjectAltName\n\n# To add a new IP/hostname, regenerate:\nkubeadm init phase certs apiserver \\\n  --apiserver-cert-extra-sans=lb.mycompany.com",
      desc: "Added a load balancer or new hostname after cluster creation? The cert SANs must include it or connections get 'certificate is not valid for this hostname'.",
    },
    {
      icon: "🔑", color: COLORS.accent,
      title: "Rotating sa.key breaks all pods",
      fix: "# DO NOT rotate sa.key without a transition plan!\n# All existing SA tokens are immediately invalidated\n# Every pod calling the API Server breaks at once\n\n# Safe rotation: configure dual key acceptance first\n# --service-account-signing-key-file=new.key\n# --service-account-key-file=old.pub,new.pub\n# Then gradually restart all pods, then remove old key",
      desc: "Every pod has a ServiceAccount JWT signed by sa.key. Rotate it without a dual-key window and every pod in the cluster breaks simultaneously. Plan extremely carefully.",
    },
    {
      icon: "📁", color: COLORS.teal,
      title: "Forgetting to restart after renewal",
      fix: "# After kubeadm certs renew all:\n# Restart static pod components\nmv /etc/kubernetes/manifests/*.yaml /tmp/\nsleep 20\nmv /tmp/*.yaml /etc/kubernetes/manifests/\n\n# Also update kubeconfig if admin.conf changed\nkubeadm init phase kubeconfig admin",
      desc: "kubeadm certs renew updates files on disk — but running processes still use the old in-memory certs. Restart is mandatory or the renewal has zero effect.",
    },
    {
      icon: "🗝️", color: COLORS.danger,
      title: "Exposing ca.key to an attacker",
      fix: "# Verify ca.key is root-only, mode 0600\nls -la /etc/kubernetes/pki/ca.key\n# Must show: -rw------- 1 root root\n\n# If compromised: full cluster re-bootstrap required\n# No partial recovery path exists for CA key compromise",
      desc: "Whoever holds ca.key can forge ANY certificate: admin user, API Server, etcd client — anything. CA key compromise means total cluster teardown. There is no safe partial recovery.",
    },
  ];

  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: FONT, fontSize: 18, color: COLORS.cyan, marginBottom: 6, letterSpacing: 2 }}>
        06 — COMMON MISTAKES & FIXES
      </h2>
      <p style={{ fontFamily: FONT_BODY, color: COLORS.textMuted, fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
        These are the real-world incidents that take clusters down. Know them before they hit you.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {mistakes.map((m, i) => {
          const isOpen = open === i;
          return (
            <div key={i} onClick={() => setOpen(isOpen ? null : i)} style={{
              background: isOpen ? `${m.color}0d` : COLORS.bgCard,
              border: `1px solid ${isOpen ? m.color : COLORS.border}`,
              borderLeft: `3px solid ${m.color}`,
              borderRadius: 8, padding: "12px 16px", cursor: "pointer", transition: "all 0.3s",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 20 }}>{m.icon}</span>
                <span style={{ fontFamily: FONT, fontSize: 13, color: m.color, fontWeight: 600, flex: 1 }}>{m.title}</span>
                <span style={{ fontSize: 12, color: COLORS.textDim, transform: isOpen ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.3s" }}>▾</span>
              </div>
              {isOpen && (
                <div style={{ marginTop: 12 }}>
                  <p style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.textMuted, lineHeight: 1.65, marginBottom: 10 }}>
                    ⚠️ {m.desc}
                  </p>
                  <div style={{
                    background: "#000", borderRadius: 6, padding: "10px 14px",
                    fontFamily: FONT, fontSize: 11.5, color: COLORS.success, whiteSpace: "pre-wrap",
                    lineHeight: 1.8, border: `1px solid ${COLORS.border}`,
                  }}>
                    {m.fix}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── SECTION 7: GOLDEN RULES ───────────────────────────────────────────────────
function RulesSection() {
  const rules = [
    { icon: "🏛️", text: "NEVER expose ca.key — CA compromise means full cluster re-bootstrap", color: COLORS.danger },
    { icon: "📅", text: "Certs expire after 1 year — schedule renewal or deploy cert-manager", color: COLORS.warning },
    { icon: "🔁", text: "Always restart control plane pods after running kubeadm certs renew", color: COLORS.accent },
    { icon: "🪪", text: "CN = K8s username, O = K8s group — wrong values = RBAC never applies", color: COLORS.purple },
    { icon: "🌐", text: "Add ALL hostnames and IPs to apiserver SANs at cluster creation time", color: COLORS.cyan },
    { icon: "🔍", text: "Run kubeadm certs check-expiration monthly in production clusters", color: COLORS.success },
    { icon: "🗄️", text: "etcd has its OWN separate CA — independent from the cluster CA", color: COLORS.teal },
    { icon: "🤖", text: "sa.key rotation invalidates ALL ServiceAccount tokens — plan a transition window", color: COLORS.danger },
  ];

  return (
    <div style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: FONT, fontSize: 18, color: COLORS.cyan, marginBottom: 6, letterSpacing: 2 }}>
        07 — GOLDEN RULES
      </h2>
      <p style={{ fontFamily: FONT_BODY, color: COLORS.textMuted, fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
        Memorize these — CKA exam and production both test them.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        {rules.map((r, i) => (
          <div key={i} style={{
            background: COLORS.bgCard, border: `1px solid ${COLORS.border}`,
            borderRadius: 8, padding: "12px 14px", display: "flex", alignItems: "flex-start", gap: 10,
            borderLeft: `3px solid ${r.color}`,
          }}>
            <span style={{ fontSize: 20, minWidth: 28, textAlign: "center" }}>{r.icon}</span>
            <span style={{ fontFamily: FONT_BODY, fontSize: 12.5, color: COLORS.text, lineHeight: 1.5 }}>{r.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── SECTION 8: QUIZ ───────────────────────────────────────────────────────────
function QuizSection() {
  const questions = [
    {
      q: "Where does kubeadm store the cluster CA certificate and private key?",
      opts: ["/etc/kubernetes/ca.crt", "/etc/kubernetes/pki/ca.crt and ca.key", "/var/lib/kubernetes/pki/ca.crt", "/root/.kube/ca.crt"],
      ans: 1,
      exp: "/etc/kubernetes/pki/ is the PKI directory kubeadm creates. ca.crt is the public cert; ca.key is the private key. Both live on the master node — ca.key must never leave.",
    },
    {
      q: "A user certificate has CN=alice and O=backend-team. What does Kubernetes use 'backend-team' for?",
      opts: ["It is the namespace the user can access", "It maps to an RBAC group name for RoleBinding subjects", "It is the cluster name", "It defines the allowed API verbs"],
      ans: 1,
      exp: "O (Organization) maps directly to Kubernetes RBAC groups. A ClusterRoleBinding with subjects[].kind=Group and name=backend-team grants alice those permissions automatically.",
    },
    {
      q: "After running 'kubeadm certs renew all', what must you do next?",
      opts: ["Nothing — it auto-restarts the pods", "Run kubectl apply to update Secrets", "Restart control plane components so they reload certs from disk", "Drain and re-join worker nodes"],
      ans: 2,
      exp: "kubeadm certs renew writes new cert files to disk, but kube-apiserver, scheduler, and controller-manager still hold old certs in memory. You must restart them to pick up the new files.",
    },
    {
      q: "What is the key difference between TLS and mTLS?",
      opts: ["mTLS uses stronger encryption algorithms", "In mTLS both client AND server present certificates; in TLS only the server does", "mTLS is only used for etcd communication", "mTLS uses symmetric keys only"],
      ans: 1,
      exp: "Regular TLS: only the server proves identity (like a website). mTLS: BOTH sides show certificates. All Kubernetes component-to-component communication uses mTLS so both sides are verified.",
    },
    {
      q: "Why does etcd use a completely separate CA from the cluster CA?",
      opts: ["It is a kubeadm technical limitation", "To isolate etcd trust — compromise of the cluster CA does not automatically compromise etcd", "etcd does not support TLS", "For performance reasons"],
      ans: 1,
      exp: "etcd stores ALL cluster state. Its own CA means a leaked cluster CA key still cannot forge etcd client certificates. This is defense-in-depth — two separate trust domains.",
    },
    {
      q: "What happens if you rotate sa.key without a transition period?",
      opts: ["Nothing — pods auto-refresh their tokens", "Only new pods are affected", "All existing ServiceAccount tokens are immediately invalidated — every running pod breaks", "Only etcd is affected"],
      ans: 2,
      exp: "Every pod has a ServiceAccount JWT token signed with sa.key. Rotate the key and all old signatures become invalid. Every pod calling the API Server fails immediately across the whole cluster.",
    },
  ];

  const [qIdx, setQIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [answered, setAnswered] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [done, setDone] = useState(false);

  const q = questions[qIdx];

  function pick(i) {
    if (answered) return;
    setAnswered(true);
    setChosen(i);
    if (i === q.ans) setScore(s => s + 1);
  }

  function next() {
    if (qIdx + 1 >= questions.length) { setDone(true); return; }
    setQIdx(qIdx + 1);
    setAnswered(false);
    setChosen(null);
  }

  function restart() {
    setQIdx(0); setScore(0); setAnswered(false); setChosen(null); setDone(false);
  }

  if (done) {
    const pct = Math.round((score / questions.length) * 100);
    const color = pct >= 80 ? COLORS.success : pct >= 60 ? COLORS.warning : COLORS.danger;
    return (
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ fontFamily: FONT, fontSize: 18, color: COLORS.cyan, marginBottom: 6, letterSpacing: 2 }}>
          08 — QUIZ RESULTS
        </h2>
        <div style={{ background: COLORS.bgCard, border: `1px solid ${color}`, borderRadius: 12, padding: 32, textAlign: "center" }}>
          <div style={{ fontSize: 56, marginBottom: 8 }}>
            {pct >= 80 ? "🏆" : pct >= 60 ? "📚" : "🔄"}
          </div>
          <div style={{ fontFamily: FONT, fontSize: 28, color, marginBottom: 8 }}>
            {score} / {questions.length}
          </div>
          <div style={{ fontFamily: FONT_BODY, fontSize: 14, color: COLORS.textMuted, marginBottom: 24 }}>
            {pct >= 80 ? "Excellent! You are CKA-ready on TLS certificates." : pct >= 60 ? "Good — review the sections covering your missed questions." : "Review sections 03 through 06 then try again."}
          </div>
          <button onClick={restart} style={{
            fontFamily: FONT, fontSize: 13, padding: "10px 24px", borderRadius: 6,
            border: "none", cursor: "pointer", background: COLORS.accent, color: "#fff", fontWeight: 700,
          }}>
            ↺ Retry Quiz
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 20 }}>
      <h2 style={{ fontFamily: FONT, fontSize: 18, color: COLORS.cyan, marginBottom: 6, letterSpacing: 2 }}>
        08 — QUIZ
      </h2>
      <p style={{ fontFamily: FONT_BODY, color: COLORS.textMuted, fontSize: 14, marginBottom: 16, lineHeight: 1.6 }}>
        Test your understanding — {questions.length} CKA-style questions.
      </p>
      <div style={{ background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.textDim }}>Q {qIdx + 1} / {questions.length}</span>
          <span style={{ fontFamily: FONT, fontSize: 11, color: COLORS.success }}>Score: {score}</span>
        </div>
        <div style={{ height: 3, background: COLORS.border, borderRadius: 2, marginBottom: 20, overflow: "hidden" }}>
          <div style={{ width: `${(qIdx / questions.length) * 100}%`, height: "100%", background: COLORS.cyan, borderRadius: 2 }} />
        </div>

        <p style={{ fontFamily: FONT_BODY, fontSize: 14, color: COLORS.text, lineHeight: 1.65, marginBottom: 16 }}>
          {q.q}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {q.opts.map((opt, i) => {
            let bg = COLORS.bgCardAlt;
            let brd = COLORS.border;
            let col = COLORS.textMuted;
            if (answered) {
              if (i === q.ans) { bg = COLORS.successGlow; brd = COLORS.success; col = COLORS.success; }
              else if (i === chosen) { bg = COLORS.dangerGlow; brd = COLORS.danger; col = COLORS.danger; }
            }
            return (
              <div key={i} onClick={() => pick(i)} style={{
                background: bg, border: `1px solid ${brd}`, borderRadius: 8,
                padding: "10px 14px", cursor: answered ? "default" : "pointer",
                fontFamily: FONT_BODY, fontSize: 13, color: col, lineHeight: 1.5, transition: "all 0.25s",
              }}>
                {opt}
              </div>
            );
          })}
        </div>

        {answered && (
          <div style={{
            background: chosen === q.ans ? COLORS.successGlow : COLORS.dangerGlow,
            border: `1px solid ${chosen === q.ans ? COLORS.success : COLORS.danger}`,
            borderRadius: 8, padding: "10px 14px", marginBottom: 14,
            fontFamily: FONT_BODY, fontSize: 12.5,
            color: chosen === q.ans ? COLORS.success : COLORS.danger,
            lineHeight: 1.6,
          }}>
            {chosen === q.ans ? "✓ Correct! " : "✗ Not quite. "}{q.exp}
          </div>
        )}

        {answered && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button onClick={next} style={{
              fontFamily: FONT, fontSize: 13, padding: "8px 20px", borderRadius: 6,
              border: "none", cursor: "pointer", background: COLORS.accent, color: "#fff", fontWeight: 700,
            }}>
              {qIdx + 1 >= questions.length ? "See Results →" : "Next →"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── SIDEBAR NAVIGATION ────────────────────────────────────────────────────────
const NAV = [
  { id: "concepts",  icon: "🪪", label: "Concepts" },
  { id: "analogy",   icon: "🛂", label: "Passport Analogy" },
  { id: "pki",       icon: "🏛️", label: "PKI Trust Chain" },
  { id: "handshake", icon: "🔐", label: "mTLS Handshake" },
  { id: "commands",  icon: "⌨️", label: "Commands & Flags" },
  { id: "mistakes",  icon: "⚠️", label: "Common Mistakes" },
  { id: "rules",     icon: "📜", label: "Golden Rules" },
  { id: "quiz",      icon: "🎯", label: "Quiz" },
];

// ── MAIN APP ──────────────────────────────────────────────────────────────────
export default function TLSCertificatesGuide() {
  const [active, setActive] = useState("concepts");

  const sections = {
    concepts: <ConceptSection />,
    analogy: <AnalogySection />,
    pki: <PKIExplorer />,
    handshake: <HandshakeSimulator />,
    commands: <CommandsSection />,
    mistakes: <MistakesSection />,
    rules: <RulesSection />,
    quiz: <QuizSection />,
  };

  return (
    <div style={layout.app}>
      <div style={layout.sidebar}>
        <div style={{ padding: "20px 16px 12px", borderBottom: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 11, color: COLORS.cyan, fontWeight: 700, textTransform: "uppercase", letterSpacing: 2, marginBottom: 4 }}>
            Kubernetes
          </div>
          <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>TLS Certificates</div>
          <div style={{ display: "flex", gap: 4, marginTop: 8, flexWrap: "wrap" }}>
            {["PKI", "mTLS", "CKA"].map((t) => (
              <span key={t} style={layout.badge(COLORS.accent)}>{t}</span>
            ))}
          </div>
        </div>
        <nav style={{ padding: "10px 0" }}>
          {NAV.map((n) => (
            <div key={n.id} style={layout.navItem(active === n.id)} onClick={() => setActive(n.id)}>
              <span style={{ fontSize: 16 }}>{n.icon}</span>
              <span>{n.label}</span>
            </div>
          ))}
        </nav>
        <div style={{ padding: "12px 16px", borderTop: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 11, color: COLORS.textDim, lineHeight: 1.6 }}>
            PKI dir:<br /><code style={{ color: COLORS.textMuted, fontFamily: FONT }}>/etc/kubernetes/pki/</code>
          </div>
        </div>
      </div>
      <div style={layout.main}>{sections[active]}</div>
    </div>
  );
}
